import {DataTypeOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {
    SurveyAnswerSentiment,
    SurveyAttentionType,
    SurveyReviewState
} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {SurveyAggregationService} from "crqlar-react-common/dist/services/aggregation/surveyAggregation.service";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {enqueueSnackbar} from "notistack";
import {useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {type ServerTableUrlStateOptions, useServerTable} from "../../common/serverTable";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import {useSurveyReviewRefresh} from "../shared/surveyQueryRefresh";
import {filterDateRange, filterEnumValues, filterNumberValues, filterTextValues} from "../shared/surveyTableFilters";
import {useAllSurveys} from "../shared/useAllSurveys";
import {toInclusiveDayRange} from "../shared/useUrlDateRange";
import {SUBMISSIONS_MAX_EXPORT_ROWS} from "./export/surveySubmissionsDataset";
import {
    surveySubmissionActionsColumn,
    surveySubmissionReviewColumns,
    surveySubmissionSettingsColumns
} from "./surveySubmissionColumns";
import {useSurveySubmissionFilterOptions} from "./useSurveySubmissionFilterOptions";

const SENTIMENTS = Object.values(SurveyAnswerSentiment);
const REVIEW_STATES = Object.values(SurveyReviewState);
const ATTENTION_TYPES = Object.values(SurveyAttentionType);

// `?surveyId=` is how other screens open a submissions list scoped to one survey.
const URL_STATE: ServerTableUrlStateOptions = {
    filterAliases: [{
        queryParam: 'surveyId',
        field: 'surveyId',
        operator: DataTypeOperator.IN
    }]
};

interface Options {
    tableId: string
    /**
     * The period of a host that owns a date control. Without one, the period is an ordinary
     * "Submitted" filter rule and the list starts with everything.
     */
    range?: DateRange
    /**
     * The module's Submissions tab: columns made for reading and resolving replies one by one -
     * the review mark first and pinned, why a reply needs a look, what the guest said.
     */
    reviewColumns?: boolean
}

/**
 * Every submission across surveys, read the same way wherever it is listed: the module's Submissions
 * tab and the settings list share the request, the filters and the dialog. The tab has review columns
 * of its own; the settings list has one column per field, for print and export.
 */
export function useSurveySubmissionsList({tableId, range, reviewColumns = false}: Options) {
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const refreshReviewData = useSurveyReviewRefresh();

    const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
    const [markingId, setMarkingId] = useState<string | null>(null);

    const {surveys} = useAllSurveys();

    // One way only from a row: reopening is a deliberate change made in the dialog.
    const markReviewed = useCallback(async (submission: AggregationSurveySubmissionListItem) => {
        setMarkingId(submission.id);
        try {
            await SurveyService.reviewSubmission({id: submission.id, reviewState: SurveyReviewState.REVIEWED});
            await refreshReviewData();
            enqueueSnackbar(
                t('Marked as resolved'),
                {
                    variant: 'success',
                    anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
                }
            );
        } catch (error) {
            console.log(error);
            enqueueSnackbar(t('The status could not be changed.'), {
                variant: 'error',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } finally {
            setMarkingId(null);
        }
    }, [refreshReviewData, t]);

    const actionsColumn = useMemo(() => surveySubmissionActionsColumn({
        t,
        readOnly,
        markingId,
        onView: setSelectedSubmissionId,
        onMarkReviewed: markReviewed
    }), [readOnly, t, markReviewed, markingId]);

    const columns = useMemo(() => surveySubmissionSettingsColumns(t, actionsColumn), [actionsColumn, t]);

    const surveysById = useMemo(() => new Map(surveys.map(survey => [survey.id, survey])), [surveys]);

    const reviewColumnDefs = useMemo(
        () => surveySubmissionReviewColumns(t, actionsColumn, columns, surveysById),
        [actionsColumn, columns, surveysById, t]
    );

    const controller = useServerTable<AggregationSurveySubmissionListItem>({
        tableId,
        columns: reviewColumns ? reviewColumnDefs : columns,
        getRowId: submission => submission.id,
        onRowClick: submission => setSelectedSubmissionId(submission.id),
        initialSorting: [{id: 'createdAt', desc: true}],
        urlState: URL_STATE,
        queryDependencies: [range],
        allRows: {maxRows: SUBMISSIONS_MAX_EXPORT_ROWS},
        queryFn: async ({pagination, searchQuery, filterRules, sorting, options}) => {
            const page = await SurveyAggregationService.listSubmissions({
                surveyIds: filterTextValues(filterRules, 'surveyId'),
                // The rows on screen keep sensitive answers withheld; only the export asks for them.
                includeSensitive: options?.includeSensitive === true || undefined,
                query: searchQuery || undefined,
                languages: filterTextValues(filterRules, 'language'),
                surveyVersions: filterNumberValues(filterRules, 'surveyVersion'),
                reviewedByIds: filterTextValues(filterRules, 'reviewedById'),
                submittedAt: range ? toInclusiveDayRange(range) : filterDateRange(filterRules, 'submittedAt'),
                reviewedAt: filterDateRange(filterRules, 'reviewedAt'),
                reviewStates: filterEnumValues(filterRules, 'reviewState', REVIEW_STATES),
                sentiments: filterEnumValues(filterRules, 'sentiment', SENTIMENTS),
                attentionTypes: filterEnumValues(filterRules, 'attentionType', ATTENTION_TYPES),
                themeIds: filterTextValues(filterRules, 'themeId'),
                guestCategoryIds: filterTextValues(filterRules, 'guestCategoryId'),
                sectorIds: filterTextValues(filterRules, 'sectorId'),
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            });

            return {content: page.content ?? [], totalElements: page.totalElements ?? 0};
        }
    });

    // The export lays out one column per question and versions are numbered per survey, so both
    // only mean something while the list shows one survey.
    const surveyIds = filterTextValues(controller.filterRules, 'surveyId');
    const singleSurveyId = surveyIds?.length === 1 ? surveyIds[0] : undefined;
    const singleSurveyQuery = useQuery(
        ['survey', singleSurveyId],
        () => SurveyService.getSurveyById(singleSurveyId!),
        {enabled: !!singleSurveyId, refetchOnWindowFocus: false}
    );

    const singleSurveyVersion = singleSurveyId ? singleSurveyQuery.data?.version ?? 0 : 0;
    const filterOptions = useSurveySubmissionFilterOptions({surveys, hasOwnPeriod: range != null, singleSurveyVersion});

    const rows = controller.query.data?.content;
    const selectedIndex = rows?.findIndex(submission => submission.id === selectedSubmissionId) ?? -1;
    const selectedSubmission = selectedIndex >= 0 ? rows?.[selectedIndex] : undefined;
    // The next reply on this page still waiting for review, in the order the list shows them.
    const nextOpenSubmissionId = selectedIndex >= 0
        ? rows?.slice(selectedIndex + 1)
            .find(submission => submission.reviewState !== SurveyReviewState.REVIEWED)?.id
        : undefined;

    return {
        controller,
        filterOptions,
        singleSurvey: singleSurveyId ? singleSurveyQuery.data : undefined,
        selectedSubmission,
        nextOpenSubmissionId,
        openSubmission: setSelectedSubmissionId,
        closeSubmission: () => setSelectedSubmissionId(null),
    };
}
